/**
 * Altara Trust Utility · Execution Dashboard.
 *
 * Route: /platform/trust-utility
 *
 * Widgets (per Milestone 2 spec):
 *   • Requests processed
 *   • Average response time
 *   • Success rate / Failure rate / Fallback usage
 *   • Provider health matrix
 *   • Most-used providers
 *   • Recent executions
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import api from "@/lib/api";
import {
  Activity, CheckCircle2, XCircle, Repeat, Timer, Boxes,
  ArrowRight, ShieldCheck, RefreshCw,
} from "lucide-react";

const NAVY = "#0A1F44";
const GOLD = "#C9A24A";
const TEAL = "#0D9488";

const STATUS_TONE = {
  healthy:  { label: "Healthy",  bg: "#D1FAE5", color: "#065F46" },
  degraded: { label: "Degraded", bg: "#FEF3C7", color: "#92400E" },
  offline:  { label: "Offline",  bg: "#FEE2E2", color: "#7F1D1D" },
  unknown:  { label: "Unknown",  bg: "#F1F5F9", color: "#475569" },
};

const EXEC_TONE = {
  success:  { label: "Success",  bg: "#D1FAE5", color: "#065F46" },
  failure:  { label: "Failure",  bg: "#FEE2E2", color: "#7F1D1D" },
  fallback: { label: "Fallback", bg: "#FEF3C7", color: "#92400E" },
  timeout:  { label: "Timeout",  bg: "#FEE2E2", color: "#7F1D1D" },
  pending:  { label: "Pending",  bg: "#F1F5F9", color: "#334155" },
};

export default function TrustUtilityDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/trust-utility/dashboard");
      setData(data);
    } catch {
      toast.error("Failed to load Trust Utility dashboard");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function refreshHealth() {
    setRefreshing(true);
    try {
      const providers = await api.get("/trust-utility/providers").then((r) => r.data);
      await Promise.all(
        providers.map((p) => api.post(`/trust-utility/providers/${p.provider_id}/health`).catch(() => null)),
      );
      toast.success("Provider health refreshed");
      load();
    } finally {
      setRefreshing(false);
    }
  }

  if (loading) {
    return <div className="p-10 text-sm text-slate-500">Loading dashboard…</div>;
  }
  if (!data) return null;

  const succ = data.success_rate;
  const fail = data.failure_rate;

  return (
    <div className="max-w-6xl mx-auto p-6 lg:p-10 space-y-8" data-testid="trust-utility-dashboard">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: GOLD }}>
            Platform · Altara Trust Utility
          </div>
          <h1 className="font-display text-3xl font-semibold" style={{ color: NAVY }}>Execution Dashboard</h1>
          <p className="text-[15px] text-slate-600 max-w-3xl mt-2 leading-relaxed">
            Every external verification, identity, affordability, and trust service on Altara flows through
            here — one provider-neutral orchestration layer, one normalised response, one evidence chain.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="refresh-health-btn"
            onClick={refreshHealth}
            disabled={refreshing}
            className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-[13px] font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} /> Refresh health
          </button>
          <Link
            data-testid="view-providers"
            to="/platform/providers"
            className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-[13px] font-semibold text-white"
            style={{ background: NAVY }}
          >
            <Boxes className="w-4 h-4" /> Provider Marketplace
          </Link>
        </div>
      </header>

      {/* Top-row KPIs */}
      <section data-testid="tu-kpis" className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiTile icon={Activity}     label="Requests processed"  value={data.total_executions} accent={NAVY} />
        <KpiTile icon={Timer}        label="Avg response time"   value={data.avg_latency_ms != null ? `${data.avg_latency_ms} ms` : null} accent={TEAL} emptyHint="No executions yet" />
        <KpiTile icon={CheckCircle2} label="Success rate"        value={succ != null ? `${Math.round(succ * 100)}%` : null} accent="#065F46" emptyHint="No executions yet" />
        <KpiTile icon={Repeat}       label="Fallback usage"      value={data.fallback_used} accent={GOLD} />
      </section>

      {/* Success vs Failure */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="tu-secondary">
        <KpiTile icon={CheckCircle2} label="Successful"        value={data.success} accent="#065F46" />
        <KpiTile icon={XCircle}      label="Failed"            value={data.failure} accent="#7F1D1D" />
        <KpiTile icon={Repeat}       label="Failure rate"      value={fail != null ? `${Math.round(fail * 100)}%` : null} accent="#7F1D1D" emptyHint="No executions yet" />
        <KpiTile icon={Boxes}        label="Providers online"  value={Object.values(data.provider_health || {}).filter((h) => h.status === "healthy").length} accent={TEAL} />
      </section>

      {/* Provider health matrix */}
      <section data-testid="tu-provider-health">
        <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-2">
          Provider health
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {Object.entries(data.provider_health || {}).map(([pid, h]) => {
            const tone = STATUS_TONE[h.status] || STATUS_TONE.unknown;
            return (
              <Link
                key={pid}
                data-testid={`provider-health-${pid}`}
                to={`/platform/providers/${pid}`}
                className="rounded-2xl bg-white border border-slate-100 p-4 hover:border-slate-300 hover:shadow-sm transition"
              >
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4" style={{ color: NAVY }} />
                  <div className="font-semibold text-[14px] flex-1" style={{ color: NAVY }}>{pid}</div>
                  <span className="text-[9px] uppercase tracking-widest font-mono px-1.5 py-0.5 rounded"
                    style={{ background: tone.bg, color: tone.color }}>{tone.label}</span>
                </div>
                <div className="text-[11px] text-slate-500 mt-1 font-mono">
                  {h.latency_ms != null ? `${h.latency_ms}ms` : "—"}
                  {" · "}
                  {h.version || "no version"}
                  {" · "}
                  {h.last_check ? formatDistanceToNow(new Date(h.last_check), { addSuffix: true }) : "never checked"}
                </div>
                {h.errors?.length > 0 && (
                  <div className="text-[10px] text-red-700 mt-1 font-mono truncate">{h.errors[0]}</div>
                )}
              </Link>
            );
          })}
        </div>
      </section>

      {/* Most used providers */}
      <section data-testid="tu-most-used">
        <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-2">
          Most-used providers
        </div>
        {Object.keys(data.per_provider || {}).length === 0 ? (
          <div className="text-[13px] text-slate-500">No executions yet. Send a Trust Request that uses the Trust Utility to see traffic here.</div>
        ) : (
          <div className="space-y-1">
            {Object.entries(data.per_provider).map(([pid, count]) => (
              <div key={pid} className="flex items-center gap-3 rounded-xl bg-white border border-slate-100 p-3">
                <div className="font-semibold text-[13px]" style={{ color: NAVY }}>{pid}</div>
                <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-2 rounded-full" style={{
                    background: GOLD,
                    width: `${Math.min(100, (count / Math.max(1, data.total_executions)) * 100)}%`,
                  }}/>
                </div>
                <div className="text-[12px] font-mono text-slate-500">{count}</div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Recent executions */}
      <section data-testid="tu-recent">
        <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-2">
          Recent executions
        </div>
        {(data.recent_executions || []).length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-slate-200 p-6 text-center text-[13px] text-slate-500">
            No executions yet.
          </div>
        ) : (
          <div className="space-y-2">
            {data.recent_executions.map((r) => {
              const tone = EXEC_TONE[r.status] || EXEC_TONE.pending;
              return (
                <Link
                  key={r.execution_id}
                  data-testid={`exec-row-${r.execution_id}`}
                  to={`/platform/trust-utility/executions/${r.execution_id}`}
                  className="flex items-center gap-3 rounded-2xl bg-white border border-slate-100 p-3 hover:border-slate-300 hover:shadow-sm transition"
                >
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: "#F1F5F9" }}>
                    <Activity className="w-4 h-4" style={{ color: NAVY }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[13px]" style={{ color: NAVY }}>
                      {r.category} · <span className="font-mono text-[11px] text-slate-500">{r.provider_id}</span>
                    </div>
                    <div className="text-[11px] text-slate-500">
                      {r.trust_request_id ? `→ ${r.trust_request_id}` : "adhoc"}
                      {" · "}{r.latency_ms != null ? `${r.latency_ms}ms` : "—"}
                      {" · "}{r.started_at ? formatDistanceToNow(new Date(r.started_at), { addSuffix: true }) : ""}
                    </div>
                  </div>
                  <span className="text-[9px] uppercase tracking-widest font-mono px-1.5 py-0.5 rounded shrink-0"
                    style={{ background: tone.bg, color: tone.color }}>{tone.label}</span>
                  <ArrowRight className="w-4 h-4 text-slate-300" />
                </Link>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

function KpiTile({ icon: Icon, label, value, accent, emptyHint }) {
  const empty = value === null || value === undefined;
  return (
    <div
      data-testid={`tu-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className="rounded-2xl bg-white border border-slate-100 shadow-sm p-4"
    >
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-3.5 h-3.5" style={{ color: accent }} />
        <span className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{label}</span>
      </div>
      <div className="font-display text-2xl font-semibold" style={{ color: empty ? "#94A3B8" : accent }}>
        {empty ? "—" : value}
      </div>
      {empty && <div className="text-[10px] text-slate-400 mt-1">{emptyHint || "No data yet"}</div>}
    </div>
  );
}
